import { motion } from 'framer-motion';
import { usePerfume } from '../context/PerfumeContext';
import { useLanguage } from '../context/LanguageContext';
import { useHaptics } from '../hooks/useHaptics';
import { essenceData } from '../data/translations';

export const EssenceItem = ({ essence }) => {
  const { addDrop, isBottleFull, formula } = usePerfume();
  const { t } = useLanguage();
  const { lightTap, dropImpact } = useHaptics();

  const drops = formula[essence.id]?.drops || 0;

  const handleClick = () => {
    if (isBottleFull) return;
    
    lightTap();
    addDrop(essence.id);
    
    // Delayed haptic for drop impact
    setTimeout(() => {
      dropImpact();
    }, 600);
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={isBottleFull}
      className="flex flex-col items-center gap-3 p-4 rounded-2xl transition-colors duration-300 hover:bg-secondary/50 disabled:opacity-50 disabled:cursor-not-allowed group min-w-[100px]"
      whileHover={{ scale: isBottleFull ? 1 : 1.02 }}
      whileTap={{ scale: isBottleFull ? 1 : 0.95 }}
    >
      {/* Essence orb */}
      <div className="relative">
        <motion.div
          className="essence-orb w-16 h-16 md:w-20 md:h-20"
          style={{ backgroundColor: essence.color }}
          whileHover={{ scale: isBottleFull ? 1 : 1.1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
        />
        
        {/* Drop count badge */}
        {drops > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-foreground text-background text-xs font-medium flex items-center justify-center"
          >
            {drops}
          </motion.div>
        )}
      </div>

      {/* Essence name */}
      <div className="text-center">
        <span className="block text-sm font-serif text-foreground tracking-wide">
          {t(`essences.${essence.id}`)}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5">
          {t(`essenceDescriptions.${essence.id}`)}
        </span>
      </div>

      {/* Note type indicator */}
      <span className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/60 px-2 py-0.5 rounded-full bg-secondary/50">
        {t(`notes.${essence.noteType}`)}
      </span>
    </motion.button>
  );
};

export const EssenceCarousel = () => {
  const { t } = useLanguage();

  return (
    <div className="w-full">
      {/* Section header */}
      <div className="flex items-center justify-between px-6 mb-4">
        <div>
          <h2 className="text-xl md:text-2xl font-serif text-foreground">
            {t('essencesTitle')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('selectEssence')}
          </p>
        </div>
        
        {/* Swipe hint (mobile) */}
        <span className="scroll-indicator md:hidden">
          <svg 
            className="w-4 h-4" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M14 5l7 7m0 0l-7 7m7-7H3" 
            />
          </svg>
          {t('swipeHint')}
        </span>
      </div>

      {/* Scrollable carousel */}
      <div 
        className="overflow-x-auto custom-scrollbar pb-4 -mx-2 px-2"
        style={{ 
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <motion.div 
          className="flex gap-2 md:gap-4 px-4 md:justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {essenceData.map((essence, index) => (
            <motion.div
              key={essence.id}
              style={{ scrollSnapAlign: 'center' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 * index }}
            >
              <EssenceItem essence={essence} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};
